// src/hooks/useContactCompanyPermissions.ts
//
// GATE B5 — capability signals for the Contact and Company surfaces.
//
// Mirrors `useProjectPermissions` (Gate A), which is the established pattern:
// bundle a domain's capability checks into one hook so components never call
// `useHasCapability` with literals and never compare roles.
//
// MECHANISM — Central capabilities, never roles.
//
//   Both domains adopted ADR-0023 role-derived baselines, verified against real
//   staging fixtures before this UI was written:
//
//     role     contacts r/w/d      companies r/w/d
//     owner    true/true/true      true/true/true
//     admin    true/true/true      true/true/true
//     member   true/true/false     true/true/false
//     viewer   true/false/false    true/false/false
//
//   Central publishes read | write | delete (+ admin). It does NOT publish
//   `create` or `update`, so CRM's create/edit concepts both resolve to
//   `write` — the same lossless collapse used for Projects, and correct here
//   for the same reason: the backend's `.create` and `.update` permissions
//   carry identical role sets.
//
// FAIL-CLOSED BY CONSTRUCTION
//
//   `useCentralContext()` is null until boot reaches `ready`, and
//   `useHasCapability` returns false for a null context. Every `can*` below is
//   therefore false while capabilities are unresolved or erroring, so no
//   mutation control can flash enabled and then retract.
//
// THE SERVER REMAINS AUTHORITATIVE. These flags decide what is RENDERED;
// `requirePermission` decides what is PERMITTED. A stale or over-generous
// capability map can only ever produce a visible control that then 403s.

import { useCentralContext, useHasCapability } from '../services/centralContext';
import { FEATURE_CAPABILITIES } from '../config/featureCapabilities';

export interface ContactPermissions {
  /** `contacts.read` — may see contacts and their company affiliations. */
  canViewContacts: boolean;
  /** `contacts.write` — may create a contact. */
  canCreateContact: boolean;
  /**
   * `contacts.write` — may edit a contact AND mutate its company affiliations
   * (link, unlink, edit roleTitle, set primary) from the CONTACT surface.
   */
  canEditContact: boolean;
  /** `contacts.delete` — may delete the contact itself. Never affiliations. */
  canDeleteContact: boolean;
  /**
   * `contacts.write` — may run the Outbound contact-workspace ACTIONS
   * (research, LinkedIn research, call notes, outcome, callback). The server
   * requires `contacts.update` for them; a viewer can still READ the
   * workspace but these controls are not rendered.
   */
  canRunOutboundContactActions: boolean;
  /** False until the Central context resolves — treat as "unknown", not "denied". */
  isResolved: boolean;
}

export interface CompanyPermissions {
  /** `companies.read` — may see companies and their affiliated contacts. */
  canViewCompanies: boolean;
  /** `companies.write` — may create a company. */
  canCreateCompany: boolean;
  /**
   * `companies.write` — may edit a company AND mutate its contact affiliations
   * from the COMPANY surface.
   */
  canEditCompany: boolean;
  /** `companies.delete` — may delete the company itself. Never affiliations. */
  canDeleteCompany: boolean;
  isResolved: boolean;
}

export function useContactPermissions(): ContactPermissions {
  const context = useCentralContext();

  return {
    canViewContacts: useHasCapability(
      FEATURE_CAPABILITIES.viewContacts.domain,
      FEATURE_CAPABILITIES.viewContacts.action,
    ),
    canCreateContact: useHasCapability(
      FEATURE_CAPABILITIES.createContact.domain,
      FEATURE_CAPABILITIES.createContact.action,
    ),
    canEditContact: useHasCapability(
      FEATURE_CAPABILITIES.editContact.domain,
      FEATURE_CAPABILITIES.editContact.action,
    ),
    canDeleteContact: useHasCapability(
      FEATURE_CAPABILITIES.deleteContact.domain,
      FEATURE_CAPABILITIES.deleteContact.action,
    ),
    canRunOutboundContactActions: useHasCapability(
      FEATURE_CAPABILITIES.runOutboundContactActions.domain,
      FEATURE_CAPABILITIES.runOutboundContactActions.action,
    ),
    isResolved: context !== null,
  };
}

export function useCompanyPermissions(): CompanyPermissions {
  const context = useCentralContext();

  return {
    canViewCompanies: useHasCapability(
      FEATURE_CAPABILITIES.viewCompanies.domain,
      FEATURE_CAPABILITIES.viewCompanies.action,
    ),
    canCreateCompany: useHasCapability(
      FEATURE_CAPABILITIES.createCompany.domain,
      FEATURE_CAPABILITIES.createCompany.action,
    ),
    canEditCompany: useHasCapability(
      FEATURE_CAPABILITIES.editCompany.domain,
      FEATURE_CAPABILITIES.editCompany.action,
    ),
    canDeleteCompany: useHasCapability(
      FEATURE_CAPABILITIES.deleteCompany.domain,
      FEATURE_CAPABILITIES.deleteCompany.action,
    ),
    isResolved: context !== null,
  };
}
